import { SearchIcon, XIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { Input } from '@/components/ui/input';
import type { MembersManagementActiveFilter } from './types';

interface MembersManagementToolbarProps {
  readonly searchValue: string;
  readonly onSearchChange: (value: string) => void;
  readonly searchPlaceholder: string;
  readonly searchInputTestId?: string;
  readonly filterControl?: ReactNode;
  readonly sortControl?: ReactNode;
  readonly columnsControl?: ReactNode;
  readonly activeFilters?: readonly MembersManagementActiveFilter[];
  readonly onClearAllFilters?: () => void;
  readonly clearAllThreshold?: number;
}

export function MembersManagementToolbar({
  searchValue,
  onSearchChange,
  searchPlaceholder,
  searchInputTestId,
  filterControl,
  sortControl,
  columnsControl,
  activeFilters = [],
  onClearAllFilters,
  clearAllThreshold = 3,
}: MembersManagementToolbarProps) {
  return (
    <div className="border-b border-border/70 bg-[#f4f4f538] p-4 sm:p-5 dark:bg-muted/40">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <div className="flex w-full max-w-[42rem] items-center gap-2">
          <div className="relative min-w-0 max-w-[34rem] flex-1">
            <SearchIcon className="pointer-events-none absolute top-1/2 left-4 size-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              aria-label={searchPlaceholder}
              data-testid={searchInputTestId}
              value={searchValue}
              onChange={(event) => onSearchChange(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-12 border-border bg-background pr-11 pl-11 text-[15px] shadow-none placeholder:text-muted-foreground dark:bg-muted"
            />
            {searchValue ? (
              <button
                type="button"
                aria-label="Effacer la recherche"
                onClick={() => onSearchChange('')}
                className="absolute top-1/2 right-3 inline-flex size-7 -translate-y-1/2 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
              >
                <XIcon className="size-4" />
              </button>
            ) : null}
          </div>
          {filterControl}
        </div>

        {sortControl || columnsControl ? (
          <div className="flex items-center justify-end gap-2 xl:ml-auto">
            {sortControl}
            {columnsControl}
          </div>
        ) : null}
      </div>

      {activeFilters.length > 0 ? (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {activeFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              onClick={filter.onClear}
              className="inline-flex items-center gap-2 rounded-full border border-border bg-background px-3 py-1.5 text-sm text-foreground transition-colors hover:border-foreground/20 hover:bg-muted dark:bg-muted dark:hover:bg-muted/80"
            >
              <span>{filter.label}</span>
              <XIcon className="size-3.5 text-muted-foreground" />
            </button>
          ))}

          {onClearAllFilters && activeFilters.length >= clearAllThreshold ? (
            <button
              type="button"
              onClick={onClearAllFilters}
              className="text-sm font-medium text-black underline underline-offset-4 transition-colors hover:text-black/80 dark:text-foreground dark:hover:text-foreground/80"
            >
              Effacer tout
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
